import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { useDispatch } from 'react-redux';
import { handleGetRequest } from '../../../service/GetTemplate';
import { endPoints } from '../../../Config';
import usePreferredLanguage from '../../../utilities/usePreferredLanguage';
import { translateUiText } from '../../../utilities/siteTranslations';
import ReportExportButton from '../ReportExportButton';
import { inReportDateRange, reportDate } from '../reportFilters';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

const FailureAnalysisReport = () => {
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const [failures, setFailures] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [machineTypeOptions, setMachineTypeOptions] = useState([{ label: 'All Types', value: null }]);
    const [locationOptions, setLocationOptions] = useState([{ label: 'All Locations', value: null }]);
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        machineType: null,
        location: null
    });

    const dispatch = useDispatch();

    const formatDate = (date) => {
        if (!date) return null;
        const value = reportDate(date);
        return value ? [value.getFullYear(), String(value.getMonth() + 1).padStart(2, '0'), String(value.getDate()).padStart(2, '0')].join('-') : null;
    };

    const buildDateRangeLabel = (selectedFilters) => {
        const start = formatDate(selectedFilters.startDate);
        const end = formatDate(selectedFilters.endDate);
        if (!start && !end) return t('All time');
        if (start && end) return `${start} ${t('to')} ${end}`;
        if (start) return `${t('From')} ${start}`;
        return `${t('Until')} ${end}`;
    };

    const computeAnalysis = (source, selectedFilters = filters) => {
        const groups = {};

        source.filter((f) => inReportDateRange(f.date, selectedFilters.startDate, selectedFilters.endDate)
            && (!selectedFilters.machineType || f.machineType === selectedFilters.machineType)
            && (!selectedFilters.location || f.serviceArea === selectedFilters.location)).forEach((f) => {
            const machineType = f.machineType || t('Unknown');
            const location = f.serviceArea || t('Unknown');
            const failureType = f.failureType || t('Unknown');
            const key = `${machineType}|${location}|${failureType}`;

            if (!groups[key]) {
                groups[key] = {
                    id: key,
                    machineType,
                    location,
                    failureType,
                    count: 0,
                    percentage: 0,
                    dateRange: buildDateRangeLabel(selectedFilters)
                };
            }

            groups[key].count += 1;
        });

        const list = Object.values(groups);
        const totalCount = list.reduce((sum, item) => sum + item.count, 0) || 1;

        list.forEach((item) => {
            item.percentage = parseFloat(((item.count / totalCount) * 100).toFixed(1));
        });

        setFilteredData(list);
        return list;
    };

    useEffect(() => {
        let cancelled = false;
        const loadFailures = async () => {
            const response = await handleGetRequest(endPoints.getFailuresList(''), true, false, dispatch);
            if (cancelled) return;
            const rows = Array.isArray(response?.data) ? response.data : [];
            setFailures(rows);
            setMachineTypeOptions([{ label: t('All Types'), value: null }, ...Array.from(new Set(rows.map((row) => row.machineType).filter(Boolean))).map((type) => ({ label: t(type), value: type }))]);
            setLocationOptions([{ label: t('All Locations'), value: null }, ...Array.from(new Set(rows.map((row) => row.serviceArea).filter(Boolean))).map((location) => ({ label: t(location), value: location }))]);
            computeAnalysis(rows);
        };
        loadFailures();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilters = () => computeAnalysis(failures, filters);

    const resetFilters = () => {
        const cleared = { startDate: null, endDate: null, machineType: null, location: null };
        setFilters(cleared);
        computeAnalysis(failures, cleared);
    };

    const percentageBodyTemplate = (rowData) => `${rowData.percentage}%`;

    // Get most common failure type
    const mostCommonFailure = filteredData.length > 0
        ? filteredData.reduce((prev, current) => 
            (prev.count > current.count) ? prev : current
          )
        : null;

    return (
        <div>
            <div className="card mb-4">
                <h4 className="mb-3">{t('Filters')}</h4>
                <div className="grid">
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Start Date')}</label>
                        <Calendar
                            value={filters.startDate}
                            onChange={(e) => setFilters({ ...filters, startDate: e.value })}
                            dateFormat="yy-mm-dd"
                            placeholder={t('Select Start Date')}
                            showIcon
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('End Date')}</label>
                        <Calendar
                            value={filters.endDate}
                            onChange={(e) => setFilters({ ...filters, endDate: e.value })}
                            dateFormat="yy-mm-dd"
                            placeholder={t('Select End Date')}
                            showIcon
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Machine Type')}</label>
                        <Dropdown
                            value={filters.machineType}
                            options={machineTypeOptions}
                            onChange={(e) => setFilters({ ...filters, machineType: e.value })}
                            placeholder={t('Select Machine Type')}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Location')}</label>
                        <Dropdown
                            value={filters.location}
                            options={locationOptions}
                            onChange={(e) => setFilters({ ...filters, location: e.value })}
                            placeholder={t('Select Location')}
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="flex gap-2 mt-3">
                    <Button label={t('Apply Filters')} icon="pi pi-filter" onClick={applyFilters} className="p-button-primary" />
                    <Button label={t('Reset')} icon="pi pi-refresh" onClick={resetFilters} className="p-button-secondary" />
                </div>
            </div>

            {mostCommonFailure && (
                <div className="grid mb-4">
                    <div className="col-12">
                        <Card>
                            <h5>{t('Most Common Failure Type')}</h5>
                            <p className="text-2xl font-bold text-primary mt-2">
                                {t(mostCommonFailure.failureType)}
                            </p>
                            <p className="text-color-secondary">
                                {t('For')} {t(mostCommonFailure.machineType)} {t('at')} {t(mostCommonFailure.location)}
                            </p>
                            <p className="text-color-secondary">
                                {mostCommonFailure.count} {t('occurrences')} ({mostCommonFailure.percentage}%)
                            </p>
                        </Card>
                    </div>
                </div>
            )}

            <div className="card">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h4>{t('Failure Analysis Report')}</h4>
                    <ReportExportButton getRows={applyFilters} filename="Analisis_de_fallas" columns={[
                        { key: 'machineType', header: t('Machine Type'), formatter: t },
                        { key: 'location', header: t('Location'), formatter: t },
                        { key: 'failureType', header: t('Failure Type'), formatter: t },
                        { key: 'count', header: t('Count') },
                        { key: 'percentage', header: t('Percentage') },
                        { key: 'dateRange', header: t('Date Range'), width: 30 }
                    ]} />
                </div>
                <DataTable
                    value={filteredData}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 20]}
                    emptyMessage={t('No data available.')}
                    responsiveLayout="scroll"
                >
                    <Column field="machineType" header={t('Machine Type')} body={(rowData) => t(rowData.machineType)} />
                    <Column field="location" header={t('Location')} body={(rowData) => t(rowData.location)} />
                    <Column field="failureType" header={t('Failure Type')} body={(rowData) => t(rowData.failureType)} />
                    <Column field="count" header={t('Count')} />
                    <Column field="percentage" header={t('Percentage')} body={percentageBodyTemplate} />
                    <Column field="dateRange" header={t('Date Range')} />
                </DataTable>
            </div>
        </div>
    );
};

export default FailureAnalysisReport;
